import {hasPermission} from 'common/HelperFunctions'
import {calculateErrorMessage} from 'common/helpers'

import type {
  WalkthroughSessionMeta,
  WalkthroughState,
  WalkthroughStep,
} from '../core/DynamicWalkthroughContext'
import {createSmartNavigator} from './SmartRouteNavigator'
import {performFullWalkthroughCleanup} from './cleanupHelpers'
import * as Persistence from './persistence'
import {
  findAndTagElement,
  injectWalkthroughAttributes,
  observeForSelector,
  resolveWalkthroughTargetSync,
  scrollToElement,
} from './walkthroughDom'
import type {DOMCache} from './walkthroughDomCache'
import {buildDomCache, documentReadyHelper} from './walkthroughDomCache'
import {
  getQuickFindOptions,
  isElementObstructed,
  waitForElementUnblocked,
} from './walkthroughProviderDomHelpers'
import {getComponentFromSteps} from './walkthroughRouteRegistry'

const isStepPermitted = (step: WalkthroughStep): boolean => {
  const perm = step.walkthrough.permission
  if (!perm) return true
  if (Array.isArray(perm)) {
    return perm.some((p) => hasPermission(p))
  }
  return hasPermission(perm)
}

const findNextPermittedIndex = (steps: WalkthroughStep[], startIndex: number): number => {
  for (let i = Math.max(0, startIndex); i < steps.length; i++) {
    if (isStepPermitted(steps[i])) return i
  }
  return -1
}

export type ControllerContext = {
  state: WalkthroughState
  setState: (s: any) => void
  domCache: DOMCache | null
  setDomCache: (c: DOMCache | null) => void
  sessionMeta: WalkthroughSessionMeta
  refs: {
    startingPathRef: {current: string}
    restorationAttempted: {current: boolean}
    walkthroughInProgress: {current: boolean}
    postNavigationInProgress: {current: boolean}
    lastEventTimestamp: {current: number}
    modalOpenedByWalkthrough: {current: boolean}
  }
  helpers: {
    closeChatIfOpen: () => void
    findAndTagElement: typeof findAndTagElement
    scrollToElement: typeof scrollToElement
    createSmartNavigator: typeof createSmartNavigator
    buildDomCache: typeof buildDomCache
    documentReadyHelper: typeof documentReadyHelper
    getComponentFromSteps: typeof getComponentFromSteps
    cleanupPreviousStep: () => void
    waitForUserAction: (target: string, action: string, stepId: string) => Promise<boolean>
    navigate: (to: string) => void
    location: any
    navigateFn: any
    intl: any
    completionMessage: string
    setIsTransitioning: (v: boolean) => void
    setCompletionToastMessage: (m: string) => void
    setCompletionToastVisible: (v: boolean) => void
  }
}

export async function startWalkthrough(
  steps: WalkthroughStep[],
  apiResponse: any,
  ctx: ControllerContext,
  resumeIndex: number = 0
) {
  if (!steps || steps.length === 0) return
  const {setState, refs, helpers, sessionMeta} = ctx
  const {walkthroughInProgress, postNavigationInProgress, startingPathRef} = refs
  const safeResumeIndex = Number.isFinite(resumeIndex)
    ? Math.max(0, Math.min(resumeIndex, steps.length - 1))
    : 0
  const permittedStartIndex = findNextPermittedIndex(steps, safeResumeIndex)

  if (walkthroughInProgress.current) return
  walkthroughInProgress.current = true

  if (permittedStartIndex === -1) {
    setState((prev: any) => ({
      ...prev,
      isNavigating: false,
      error: 'No permitted walkthrough steps are available for this user.',
    }))
    walkthroughInProgress.current = false
    return
  }

  const waitForCompletion = async (
    completion: NonNullable<WalkthroughStep['walkthrough']['completion']>
  ): Promise<boolean> => {
    const timeout = completion.timeout ?? 8000

    const waitForElement = async (sel: string) => {
      const start = Date.now()
      const check = () => !!resolveWalkthroughTargetSync(sel)
      if (check()) return true
      return new Promise<boolean>((resolve) => {
        const id = setInterval(() => {
          try {
            if (check()) {
              clearInterval(id)
              resolve(true)
            } else if (Date.now() - start > timeout) {
              clearInterval(id)
              resolve(false)
            }
          } catch (e) {
            clearInterval(id)
            resolve(false)
            calculateErrorMessage(e)
          }
        }, 200)
      })
    }

    const waitForRoute = async (target: string) => {
      if (!ctx.helpers || !ctx.helpers.location) return false
      const start = Date.now()
      const matchNow = () => {
        try {
          const path = String(ctx.helpers.location.pathname || '')
          return target === path
        } catch (e) {
          calculateErrorMessage(e)
          return false
        }
      }
      if (matchNow()) return true
      return new Promise<boolean>((resolve) => {
        const onPop = () => {
          if (matchNow()) {
            window.removeEventListener('popstate', onPop)
            resolve(true)
          } else if (Date.now() - start > timeout) {
            window.removeEventListener('popstate', onPop)
            resolve(false)
          }
        }
        window.addEventListener('popstate', onPop)
        const id = setInterval(() => {
          if (matchNow()) {
            clearInterval(id)
            window.removeEventListener('popstate', onPop)
            resolve(true)
          } else if (Date.now() - start > timeout) {
            clearInterval(id)
            window.removeEventListener('popstate', onPop)
            resolve(false)
          }
        }, 200)
      })
    }

    const waitForMutation = async (sel: string) => {
      if (resolveWalkthroughTargetSync(sel)) return true
      return new Promise<boolean>((resolve) => {
        const observer = new MutationObserver(() => {
          if (resolveWalkthroughTargetSync(sel)) {
            observer.disconnect()
            resolve(true)
          }
        })
        observer.observe(document.body, {childList: true, subtree: true, characterData: true})
        setTimeout(() => {
          observer.disconnect()
          resolve(false)
        }, timeout)
      })
    }

    try {
      switch (completion.type) {
        case 'element':
          return await waitForElement(completion.target)
        case 'route':
          return await waitForRoute(completion.target)
        case 'mutation':
          return await waitForMutation(completion.target)
        default:
          return false
      }
    } catch (e) {
      calculateErrorMessage(e)
      return false
    }
  }

  try {
    helpers.closeChatIfOpen()
    try {
      startingPathRef.current = String(window.location.pathname || '')
    } catch (e) {
      startingPathRef.current = ctx.helpers.location.pathname
      calculateErrorMessage(e)
    }

    setState({
      isActive: true,
      steps: steps.map((s) => ({
        ...s,
        walkthrough: {
          ...s.walkthrough,
          tooltipMode: s.walkthrough.tooltipMode || 'blocking',
        },
      })),
      currentStepIndex: permittedStartIndex,
      currentElement: null,
      isNavigating: true,
      error: null,
    })

    const componentInfo = helpers.getComponentFromSteps(steps)
    if (!componentInfo) {
      setState((prev: any) => ({
        ...prev,
        isNavigating: false,
        error: 'Could not determine target component from steps',
      }))
      return
    }

    if (apiResponse) {
      try {
        Persistence.savePendingWalkthrough(apiResponse, permittedStartIndex, sessionMeta)
      } catch (e) {
        calculateErrorMessage(e)
      }
    }

    const navigator = helpers.createSmartNavigator(helpers.navigateFn, helpers.location, {
      verbose: true,
    })
    const navResult = await navigator.navigateToComponent(componentInfo)

    if (!navResult.success) {
      setState((prev: any) => ({
        ...prev,
        isNavigating: false,
        error: navResult.message || 'Navigation failed',
      }))
      try {
        sessionStorage.removeItem('pending-walkthrough')
      } catch (e) {
        calculateErrorMessage(e)
      }
      walkthroughInProgress.current = false
      return
    }

    if (postNavigationInProgress.current) return
    postNavigationInProgress.current = true

    await helpers.documentReadyHelper()

    try {
      const cache = helpers.buildDomCache(document.body)
      ctx.setDomCache(cache)
    } catch (err) {
      calculateErrorMessage(err)
      ctx.setDomCache(null)
    }

    try {
      const firstStep = steps[permittedStartIndex] || steps[0]
      setState((prev: any) => ({...prev, isNavigating: false}))

      let elementResult: any = {success: false, element: null}
      try {
        const maxAttempts = 3
        let attempt = 0
        while (attempt < maxAttempts) {
          attempt++
          try {
            try {
              ctx.setDomCache(helpers.buildDomCache(document.body))
            } catch (e) {
              calculateErrorMessage(e)
            }
            elementResult = await helpers.findAndTagElement(
              firstStep.walkthrough.target,
              firstStep.id,
              getQuickFindOptions(ctx.domCache)
            )
          } catch (err) {
            calculateErrorMessage(err)
            elementResult = {success: false, element: null}
          }
          if (elementResult && elementResult.success && elementResult.element) break
          if (attempt < maxAttempts) await new Promise((r) => setTimeout(r, 500))
        }
      } catch (error) {
        setState((prev: any) => ({
          ...prev,
          isNavigating: false,
          error: String(error),
        }))
        walkthroughInProgress.current = false
        postNavigationInProgress.current = false
        try {
          sessionStorage.removeItem('pending-walkthrough')
        } catch (e) {
          calculateErrorMessage(e)
        }
        return
      }

      if (!elementResult.success || !elementResult.element) {
        try {
          const observed = await observeForSelector(firstStep.walkthrough.target, {
            root: document.body,
            timeout: 12000,
            pollInterval: 150,
          })
          if (observed.success && observed.element) {
            injectWalkthroughAttributes(observed.element, firstStep.id)
            elementResult = {
              success: true,
              element: observed.element,
            }
          }
        } catch (err) {
          calculateErrorMessage(err)
        }
      }

      if (!elementResult.success || !elementResult.element) {
        setState((prev: any) => ({
          ...prev,
          isNavigating: false,
          error: `Could not find element: ${firstStep.walkthrough.target}`,
        }))
        postNavigationInProgress.current = false
        try {
          sessionStorage.removeItem('pending-walkthrough')
        } catch (e) {
          calculateErrorMessage(e)
        }
        await nextStep(ctx)
        return
      }

      const foundEl = elementResult.element as HTMLElement
      if (firstStep.walkthrough.scroll !== 'none') helpers.scrollToElement(foundEl)

      setState((prev: any) => ({
        ...prev,
        currentElement: foundEl,
        isNavigating: false,
        error: null,
      }))
      Persistence.updatePendingWalkthroughProgress(permittedStartIndex, sessionMeta)
      try {
        sessionStorage.removeItem('pending-walkthrough')
      } catch (e) {
        calculateErrorMessage(e)
      }

      walkthroughInProgress.current = false
      postNavigationInProgress.current = false

      const requiresUserAction = firstStep.walkthrough.action !== 'none'
      if (requiresUserAction) {
        const targetEl = elementResult.element
        try {
          const obstructed = isElementObstructed(targetEl)
          if (obstructed) {
            setState((prev: any) => ({
              ...prev,
              error:
                'A modal or dialog is blocking the highlighted element. Please close it, then continue.',
            }))
            const unblocked = await waitForElementUnblocked(targetEl, 30000)
            if (!unblocked) {
              setState((prev: any) => ({
                ...prev,
                error:
                  'Still blocked: please close the modal blocking the highlighted element, or press Next to skip this step.',
              }))
            } else {
              setState((prev: any) => ({...prev, error: null}))
            }
          }
        } catch (err) {
          calculateErrorMessage(err)
        }

        if (!isElementObstructed(targetEl)) {
          const actionSuccess = await helpers.waitForUserAction(
            firstStep.walkthrough.target,
            firstStep.walkthrough.action,
            firstStep.id
          )
          if (actionSuccess) {
            helpers.cleanupPreviousStep()

            const completion = firstStep.walkthrough.completion
            if (completion) {
              setState((prev: any) => ({...prev, error: null}))
              const ok = await waitForCompletion(completion)
              if (!ok) {
                console.warn('Completion condition timed out for step', firstStep.id, completion)
                setState((prev: any) => ({
                  ...prev,
                  error: `Completion timeout for ${firstStep.id}`,
                }))
              }
            }

            await nextStep(ctx)
          }
        }
      }
    } catch (innerError) {
      setState((prev: any) => ({
        ...prev,
        isNavigating: false,
        error: String(innerError),
      }))
      walkthroughInProgress.current = false
      postNavigationInProgress.current = false
      try {
        sessionStorage.removeItem('pending-walkthrough')
      } catch (e) {
        calculateErrorMessage(e)
      }
      return
    }
  } catch (error) {
    setState((prev: any) => ({
      ...prev,
      isNavigating: false,
      error: String(error),
    }))
    walkthroughInProgress.current = false
    try {
      sessionStorage.removeItem('pending-walkthrough')
    } catch (e) {
      calculateErrorMessage(e)
    }
  }
}

export async function nextStep(ctx: ControllerContext) {
  const {state, setState, refs, helpers, domCache, sessionMeta} = ctx as any
  const {startingPathRef} = refs
  if (state.currentStepIndex >= state.steps.length - 1) {
    performFullWalkthroughCleanup({
      refs: ctx.refs,
      helpers: ctx.helpers,
      setState,
      reason: 'nextStep reached last step',
    })
    performFullWalkthroughCleanup({
      refs: ctx.refs,
      helpers: ctx.helpers,
      setState,
      reason: 'nextStep reached last step',
    })

    if (startingPathRef.current && startingPathRef.current !== helpers.location.pathname) {
      helpers.navigate(startingPathRef.current)
    }
    startingPathRef.current = ''

    const msg = helpers.completionMessage
    try {
      helpers.setCompletionToastMessage(msg)
      helpers.setCompletionToastVisible(true)
      setTimeout(() => helpers.setCompletionToastVisible(false), 3500)
    } catch (e) {
      calculateErrorMessage(e)
    }
    window.dispatchEvent(
      new CustomEvent('dynamic-walkthrough-end', {
        detail: {completed: true, message: msg, sessionMeta},
      })
    )
    return
  }

  helpers.setIsTransitioning(true)
  helpers.cleanupPreviousStep()
  await new Promise((r) => setTimeout(r, 150))

  let nextIndex = state.currentStepIndex + 1
  let attempts = 0
  const maxAttempts = state.steps.length - nextIndex

  while (attempts < maxAttempts) {
    const nextStepData = state.steps[nextIndex]
    if (!isStepPermitted(nextStepData)) {
      nextIndex++
      attempts++
      continue
    }
    const targetPath = nextStepData?.path
    const currentPath = window.location.pathname
    if (targetPath && typeof targetPath === 'string' && currentPath !== targetPath) {
      try {
        setState((prev: any) => ({...prev, isNavigating: true, currentElement: null}))
        const navigator = helpers.createSmartNavigator(helpers.navigateFn, helpers.location, {
          verbose: true,
        })
        const componentInfo = {
          componentName: `Route:${targetPath}`,
          route: targetPath,
          componentRootSelector:
            nextStepData?.walkthrough?.target || '.page-content, .container-fluid, main, body',
          readinessValidators: nextStepData?.walkthrough?.target
            ? [nextStepData.walkthrough.target]
            : ['body'],
          dataLoadingIndicators: ['.spinner-border', '.loading-spinner', '.skeleton'],
          mountTimeout: 12000,
          dataLoadTimeout: 15000,
          observerTimeout: 12000,
        }
        console.log('Per-step navigation to:', targetPath)
        const navResult = await navigator.navigateToComponent(componentInfo)
        if (!navResult.success) {
          setState((prev: any) => ({
            ...prev,
            isNavigating: false,
            error: navResult.message || 'Navigation failed',
          }))
          helpers.setIsTransitioning(false)
          return
        }

        await helpers.documentReadyHelper()
        try {
          const cache = helpers.buildDomCache(document.body)
          ctx.setDomCache(cache)
        } catch (err) {
          calculateErrorMessage(err)
          ctx.setDomCache(null)
        }
      } catch (err) {
        calculateErrorMessage(err)
        setState((prev: any) => ({...prev, isNavigating: false, error: String(err)}))
        helpers.setIsTransitioning(false)
        return
      }
    }
    setState((prev: any) => ({
      ...prev,
      currentStepIndex: nextIndex,
      isNavigating: false,
      currentElement: null,
    }))

    try {
      const elementResult = await helpers.findAndTagElement(
        nextStepData.walkthrough.target,
        nextStepData.id,
        getQuickFindOptions(domCache)
      )
      if (!elementResult.success || !elementResult.element) {
        nextIndex++
        attempts++
        if (nextIndex >= state.steps.length) {
          performFullWalkthroughCleanup({
            refs: ctx.refs,
            helpers: ctx.helpers,
            setState,
            reason: 'nextStep loop could not find elements',
          })
          performFullWalkthroughCleanup({
            refs: ctx.refs,
            helpers: ctx.helpers,
            setState,
            reason: 'nextStep loop could not find elements',
          })
          if (startingPathRef.current && startingPathRef.current !== helpers.location.pathname) {
            helpers.navigate(startingPathRef.current)
          }
          startingPathRef.current = ''

          const msg = helpers.completionMessage
          try {
            helpers.setCompletionToastMessage(msg)
            helpers.setCompletionToastVisible(true)
            setTimeout(() => helpers.setCompletionToastVisible(false), 3500)
          } catch (e) {
            calculateErrorMessage(e)
          }
          window.dispatchEvent(
            new CustomEvent('dynamic-walkthrough-end', {
              detail: {completed: true, message: msg, sessionMeta},
            })
          )
          return
        }
        continue
      }

      try {
        const newElementBlocked = isElementObstructed(elementResult.element)
        if (newElementBlocked) {
          setState((prev: any) => ({
            ...prev,
            error:
              'A modal or dialog is blocking the highlighted element. Please close it to continue the walkthrough.',
          }))
          helpers.setPortalPassThrough(true)
          const unblocked = await waitForElementUnblocked(elementResult.element, 5 * 60 * 1000)
          helpers.setPortalPassThrough(false)
          if (!unblocked) {
            helpers.setIsTransitioning(false)
            return
          }
          setState((prev: any) => ({...prev, error: null}))
        }
      } catch (err) {
        calculateErrorMessage(err)
      }

      helpers.setIsTransitioning(false)
      if (nextStepData.walkthrough.scroll !== 'none') helpers.scrollToElement(elementResult.element)

      setState((prev: any) => ({
        ...prev,
        currentElement: elementResult.element,
        isNavigating: false,
        error: null,
      }))
      Persistence.updatePendingWalkthroughProgress(nextIndex, sessionMeta)

      const requiresUserAction = nextStepData.walkthrough.action !== 'none'
      if (requiresUserAction) {
        const targetEl = elementResult.element
        try {
          const obstructed = isElementObstructed(targetEl)
          if (obstructed) {
            setState((prev: any) => ({
              ...prev,
              error: 'A modal/dialog is blocking this element. Please close it, then continue.',
            }))
            helpers.setPortalPassThrough(true)
            const unblocked = await waitForElementUnblocked(targetEl, 30000)
            helpers.setPortalPassThrough(false)
            if (!unblocked) {
              setState((prev: any) => ({
                ...prev,
                error: 'Still blocked: please close the modal or press Next to skip this step.',
              }))
            } else {
              setState((prev: any) => ({...prev, error: null}))
            }
          }
        } catch (err) {
          calculateErrorMessage(err)
        }

        if (!isElementObstructed(targetEl)) {
          const actionSuccess = await helpers.waitForUserAction(
            nextStepData.walkthrough.target,
            nextStepData.walkthrough.action,
            nextStepData.id
          )
          if (actionSuccess) {
            helpers.cleanupPreviousStep()

            const completion = nextStepData.walkthrough.completion
            if (completion) {
              setState((prev: any) => ({...prev, error: null}))
              try {
                const ok = await (async () => {
                  const timeout = completion.timeout ?? 8000
                  const start = Date.now()
                  const checkSelector = () => !!document.querySelector(completion.target)
                  if (completion.type === 'route') {
                    if (helpers.location.pathname === completion.target) return true
                    while (Date.now() - start < timeout) {
                      if (helpers.location.pathname === completion.target) return true

                      await new Promise((r) => setTimeout(r, 200))
                    }
                    return false
                  }
                  if (completion.type === 'element') {
                    if (checkSelector()) return true
                    while (Date.now() - start < timeout) {
                      if (checkSelector()) return true

                      await new Promise((r) => setTimeout(r, 200))
                    }
                    return false
                  }
                  if (completion.type === 'mutation') {
                    if (checkSelector()) return true
                    while (Date.now() - start < timeout) {
                      if (checkSelector()) return true

                      await new Promise((r) => setTimeout(r, 200))
                    }
                    return false
                  }
                  return false
                })()

                if (!ok) {
                  console.warn(
                    'Completion condition timed out for step',
                    nextStepData.id,
                    completion
                  )
                  setState((prev: any) => ({
                    ...prev,
                    error: `Completion timeout for ${nextStepData.id}`,
                  }))
                }
              } catch (e) {
                calculateErrorMessage(e)
              }
            }

            nextIndex += 1
            attempts = 0
            if (nextIndex >= state.steps.length) {
              performFullWalkthroughCleanup({
                refs: ctx.refs,
                helpers: ctx.helpers,
                setState,
                reason: 'nextStep catch block',
              })
              performFullWalkthroughCleanup({
                refs: ctx.refs,
                helpers: ctx.helpers,
                setState,
                reason: 'nextStep catch block',
              })
              if (
                startingPathRef.current &&
                startingPathRef.current !== helpers.location.pathname
              ) {
                helpers.navigate(startingPathRef.current)
              }
              startingPathRef.current = ''

              const msg = helpers.completionMessage
              try {
                helpers.setCompletionToastMessage(msg)
                helpers.setCompletionToastVisible(true)
                setTimeout(() => helpers.setCompletionToastVisible(false), 3500)
              } catch (e) {
                calculateErrorMessage(e)
              }
              window.dispatchEvent(
                new CustomEvent('dynamic-walkthrough-end', {
                  detail: {completed: true, message: msg, sessionMeta},
                })
              )
              return
            }
            setState((prev: any) => ({
              ...prev,
              currentStepIndex: nextIndex,
              currentElement: null,
              isNavigating: true,
              error: null,
            }))
            continue
          }
        }
      }
      break
    } catch (error) {
      calculateErrorMessage(error)
      nextIndex++
      attempts++
      if (nextIndex >= state.steps.length) {
        performFullWalkthroughCleanup({
          refs: ctx.refs,
          helpers: ctx.helpers,
          setState,
          reason: 'nextStep catch error path',
        })
        if (startingPathRef.current && startingPathRef.current !== helpers.location.pathname) {
          helpers.navigate(startingPathRef.current)
        }
        startingPathRef.current = ''

        const msg = helpers.completionMessage
        try {
          helpers.setCompletionToastMessage(msg)
          helpers.setCompletionToastVisible(true)
          setTimeout(() => helpers.setCompletionToastVisible(false), 3500)
        } catch (e) {
          calculateErrorMessage(e)
        }
        window.dispatchEvent(
          new CustomEvent('dynamic-walkthrough-end', {
            detail: {completed: true, message: msg, sessionMeta},
          })
        )
        return
      }
    }
  }

  if (attempts >= maxAttempts) {
    performFullWalkthroughCleanup({
      refs: ctx.refs,
      helpers: ctx.helpers,
      setState,
      reason: 'nextStep attempts exhausted',
    })
    if (startingPathRef.current && startingPathRef.current !== helpers.location.pathname) {
      helpers.navigate(startingPathRef.current)
    }
    startingPathRef.current = ''
    window.dispatchEvent(
      new CustomEvent('dynamic-walkthrough-end', {
        detail: {completed: false, sessionMeta},
      })
    )
  }
}

export async function previousStep(ctx: ControllerContext) {
  const {state, setState, helpers, domCache, sessionMeta} = ctx as any
  if (state.currentStepIndex <= 0) return

  const originalIndex = state.currentStepIndex
  let prevIndex = originalIndex - 1
  let attempts = 0
  const maxAttempts = originalIndex

  setState((prev: any) => ({
    ...prev,
    currentStepIndex: prevIndex,
    isNavigating: false,
    currentElement: null,
  }))

  while (attempts < maxAttempts && prevIndex >= 0) {
    const prevStepData = state.steps[prevIndex]
    if (!isStepPermitted(prevStepData)) {
      prevIndex--
      attempts++
      setState((prev: any) => ({
        ...prev,
        currentStepIndex: Math.max(0, prevIndex),
      }))
      continue
    }
    try {
      const elementResult = await helpers.findAndTagElement(
        prevStepData.walkthrough.target,
        prevStepData.id,
        getQuickFindOptions(domCache)
      )
      if (!elementResult.success || !elementResult.element) {
        prevIndex--
        attempts++
        setState((prev: any) => ({
          ...prev,
          currentStepIndex: Math.max(0, prevIndex),
        }))
        continue
      }

      if (prevStepData.walkthrough.scroll !== 'none') helpers.scrollToElement(elementResult.element)

      setState((prev: any) => ({
        ...prev,
        currentElement: elementResult.element,
        isNavigating: false,
        error: null,
        currentStepIndex: prevIndex,
      }))
      Persistence.updatePendingWalkthroughProgress(prevIndex, sessionMeta)
      return
    } catch (err) {
      calculateErrorMessage(err)
      prevIndex--
      attempts++
      setState((prev: any) => ({
        ...prev,
        currentStepIndex: Math.max(0, prevIndex),
      }))
    }
  }

  try {
    const originalStep = state.steps[originalIndex]
    const restoreResult = await helpers.findAndTagElement(
      originalStep.walkthrough.target,
      originalStep.id,
      getQuickFindOptions(domCache)
    )
    setState((prev: any) => ({
      ...prev,
      currentStepIndex: originalIndex,
      currentElement: restoreResult.success ? restoreResult.element : null,
      isNavigating: false,
      error: restoreResult.success
        ? null
        : `Could not find previous element(s). (${originalStep.walkthrough.target})`,
    }))
    Persistence.updatePendingWalkthroughProgress(originalIndex, sessionMeta)
  } catch (restoreErr) {
    calculateErrorMessage(restoreErr)
    setState((prev: any) => ({
      ...prev,
      currentStepIndex: originalIndex,
      currentElement: null,
      isNavigating: false,
      error: 'Could not find previous element(s).',
    }))
    Persistence.updatePendingWalkthroughProgress(originalIndex, sessionMeta)
  }
}

export async function goToStep(index: number, ctx: ControllerContext) {
  const {state, setState, domCache, helpers, sessionMeta} = ctx as any
  if (index < 0 || index >= state.steps.length) return

  const targetStep = state.steps[index]
  if (!isStepPermitted(targetStep)) {
    setState((prev: any) => ({
      ...prev,
      isNavigating: false,
      error: 'You do not have permission to access this step.',
    }))
    return
  }
  setState((prev: any) => ({
    ...prev,
    currentStepIndex: index,
    isNavigating: false,
    currentElement: null,
  }))

  try {
    const elementResult = await helpers.findAndTagElement(
      targetStep.walkthrough.target,
      targetStep.id,
      getQuickFindOptions(domCache)
    )
    if (!elementResult.success || !elementResult.element) {
      setState((prev: any) => ({
        ...prev,
        isNavigating: false,
        error: `Could not find element: ${targetStep.walkthrough.target}`,
      }))
      return
    }
    if (targetStep.walkthrough.scroll !== 'none') helpers.scrollToElement(elementResult.element)
    setState((prev: any) => ({
      ...prev,
      currentElement: elementResult.element,
      isNavigating: false,
      error: null,
    }))
    Persistence.updatePendingWalkthroughProgress(index, sessionMeta)
  } catch (error) {
    setState((prev: any) => ({
      ...prev,
      isNavigating: false,
      error: String(error),
    }))
  }
}
